import Medusa, { ClientHeaders } from "@medusajs/js-sdk"
import { authTokenStorage, getAuthToken } from "./token"

export const backendUrl = __BACKEND_URL__ ?? "/"
// export const publishableApiKey = __PUBLISHABLE_API_KEY__ ?? ""

function resolveSpecialHeaders(): ClientHeaders | undefined {
  if (__X_DEV_KEY__) {
    return {
      "X-Dev-Key": __X_DEV_KEY__,
    }
  }

  return undefined
}

export const sdk = new Medusa({
  baseUrl: backendUrl,
  globalHeaders: resolveSpecialHeaders(),
  // KAN-137: route every token read/write through the tab-scoped resolver in
  // ./token, so an impersonation session (sessionStorage) and a normal login
  // (localStorage) can never bleed into each other across tabs. Both
  // jwtTokenStorageMethod AND storage must be set - storage alone silently
  // falls back to "local" and bypasses this entirely.
  auth: {
    jwtTokenStorageMethod: "custom",
    storage: authTokenStorage,
  },
})

// useful when you want to call the BE from the console and try things out quickly
if (typeof window !== "undefined") {
  ;(window as any).__sdk = sdk
}

export const importProductsQuery = async (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return await fetch(`${backendUrl}/vendor/products/import`, {
    method: "POST",
    body: formData,
    headers: {
      authorization: `Bearer ${getAuthToken()}`,
      ...resolveSpecialHeaders(),
      // "x-publishable-api-key": publishableApiKey,
    },
  })
    .then((res) => res.json())
    .catch(() => null)
}

export const uploadFilesQuery = async (files: any[]) => {
  const formData = new FormData()

  for (const { file } of files) {
    formData.append("files", file)
  }

  return await fetch(`${backendUrl}/vendor/uploads`, {
    method: "POST",
    body: formData,
    headers: {
      authorization: `Bearer ${getAuthToken()}`,
      ...resolveSpecialHeaders(),
      // "x-publishable-api-key": publishableApiKey,
    },
  })
    .then((res) => res.json())
    .catch(() => null)
}

export const fetchQuery = async (
  url: string,
  {
    method,
    body,
    query,
    headers,
  }: {
    method: "GET" | "POST" | "DELETE"
    body?: object
    query?: Record<string, string | number | string[] | number[]>
    headers?: { [key: string]: string }
  }
) => {
  const bearer = getAuthToken()

  // Build query parameters, handling arrays properly
  const paramPairs: string[] = []
  for (const [key, value] of Object.entries(query || {})) {
    // Skip undefined, null, and empty string values
    if (value === undefined || value === null || value === "") {
      continue
    }

    // Handle arrays by repeating the parameter name
    if (Array.isArray(value)) {
      for (const item of value) {
        // Skip empty, undefined, and null items in arrays
        if (item !== undefined && item !== null && item !== "") {
          paramPairs.push(`${key}=${encodeURIComponent(item)}`)
        }
      }
    } else {
      paramPairs.push(`${key}=${encodeURIComponent(value)}`)
    }
  }

  const params = paramPairs.join("&")
  const response = await fetch(`${backendUrl}${url}${params && `?${params}`}`, {
    method: method,
    headers: {
      authorization: `Bearer ${bearer}`,
      "Content-Type": "application/json",
      ...resolveSpecialHeaders(),
      // "x-publishable-api-key": publishableApiKey,
      ...headers,
    },
    body: body ? JSON.stringify(body) : null,
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.message || "Unknown error")
  }

  return response.json()
}
